import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { API, http } from "@/lib/api";
import { toast } from "sonner";

const WELCOME =
  "Hi, I'm Izzy from NextShore. I'll take just a minute to understand your goals — may I start with your full name?";

const FIELDS = ["name", "email", "phone", "visa_type", "target_country", "timeline"];

/**
 * Very light client-side parser to extract lead fields from the running
 * transcript. Server-side Claude drives the conversation; this is a safety
 * net for capturing the values into the DB even if the model forgets fields.
 */
function extractLead(messages) {
  const userTurns = messages.filter((m) => m.role === "user").map((m) => m.content);
  const joined = userTurns.join(" | ");
  const out = {};

  const emailMatch = joined.match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
  if (emailMatch) out.email = emailMatch[0];

  const phoneMatch = joined.match(/(\+?\d[\d\s\-()]{7,}\d)/);
  if (phoneMatch) out.phone = phoneMatch[1].trim();

  const visaMap = {
    work: "Work",
    study: "Study",
    tourist: "Tourist",
    "permanent residency": "Permanent Residency",
    pr: "Permanent Residency",
    business: "Business",
    investor: "Business",
  };
  for (const [k, v] of Object.entries(visaMap)) {
    if (new RegExp(`\\b${k}\\b`, "i").test(joined)) { out.visa_type = v; break; }
  }

  const countryList = ["Canada", "United Kingdom", "UK", "Australia", "Germany", "UAE", "Dubai", "Singapore", "USA", "United States", "Ireland", "New Zealand", "Netherlands"];
  for (const c of countryList) {
    if (new RegExp(`\\b${c}\\b`, "i").test(joined)) {
      out.target_country = c === "UK" ? "United Kingdom" : c === "Dubai" ? "UAE" : c;
      break;
    }
  }

  if (/(under|less than|<)\s*3/i.test(joined) || /asap|urgent|immediately/i.test(joined)) out.timeline = "Under 3 months";
  else if (/3\s*[-–to]{1,3}\s*6/i.test(joined)) out.timeline = "3-6 months";
  else if (/6\s*[-–to]{1,3}\s*12/i.test(joined) || /within a year/i.test(joined)) out.timeline = "6-12 months";
  else if (/1\+?\s*year|more than a year|next year|no rush/i.test(joined)) out.timeline = "1+ years";

  // Name: pick the first short user turn that isn't email/phone
  if (!out.name) {
    for (const t of userTurns) {
      const stripped = t.trim();
      if (
        stripped.length >= 2 &&
        stripped.length <= 60 &&
        !stripped.includes("@") &&
        !/\d{5,}/.test(stripped) &&
        /^[A-Za-z][A-Za-z .'\-]+$/.test(stripped)
      ) {
        out.name = stripped;
        break;
      }
    }
  }

  return out;
}

export default function IzzyWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const sessionId = useRef(`izzy-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const submitLead = async (allMessages) => {
    const extracted = extractLead(allMessages);
    const missing = FIELDS.filter((f) => !extracted[f]);
    if (missing.length) return false;

    try {
      await http.post("/leads", {
        ...extracted,
        transcript: allMessages,
      });
      toast.success("Thanks! A NextShore advisor will reach out within 24 hours.");
      setSubmitted(true);
      return true;
    } catch (e) {
      toast.error("Could not save lead. Please try again.");
      return false;
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending || submitted) return;
    setInput("");

    const nextMsgs = [...messages, { role: "user", content: text }];
    setMessages([...nextMsgs, { role: "assistant", content: "" }]);
    setSending(true);

    try {
      const res = await fetch(`${API}/izzy/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId.current,
          message: text,
          history: messages,
        }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let assistantText = "";
      let done = false;

      while (!done) {
        const { value, done: streamDone } = await reader.read();
        done = streamDone;
        if (value) {
          const chunk = decoder.decode(value);
          const events = chunk.split("\n\n").filter(Boolean);
          for (const ev of events) {
            if (!ev.startsWith("data: ")) continue;
            const payload = ev.slice(6);
            if (payload === "[DONE]") { done = true; break; }
            if (payload.startsWith("[ERROR]")) {
              assistantText = "Sorry, I hit a snag. Please try again.";
              break;
            }
            assistantText += payload;
            setMessages((cur) => {
              const copy = [...cur];
              copy[copy.length - 1] = { role: "assistant", content: assistantText };
              return copy;
            });
          }
        }
      }

      const finalMessages = [...nextMsgs, { role: "assistant", content: assistantText }];
      if (assistantText.includes("[LEAD_COMPLETE]")) {
        const cleaned = assistantText.replace(/\[LEAD_COMPLETE\]/g, "").trim();
        setMessages((cur) => {
          const copy = [...cur];
          copy[copy.length - 1] = { role: "assistant", content: cleaned };
          return copy;
        });
        await submitLead([...nextMsgs, { role: "assistant", content: cleaned }]);
      } else {
        // Try opportunistic save if we already have all fields
        const extracted = extractLead(finalMessages);
        const missing = FIELDS.filter((f) => !extracted[f]);
        if (missing.length === 0 && !submitted) {
          await submitLead(finalMessages);
        }
      }
    } catch (e) {
      setMessages((cur) => {
        const copy = [...cur];
        copy[copy.length - 1] = { role: "assistant", content: "I'm having trouble connecting. Please try again shortly." };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        id="izzy-launcher"
        data-testid="izzy-chat-bubble-trigger"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full btn-gold shadow-2xl flex items-center justify-center pulse-ring"
        aria-label="Chat with Izzy"
      >
        <MessageCircle className="w-7 h-7 relative z-10" />
      </button>

      {/* Chat window */}
      {open && (
        <div
          data-testid="izzy-chat-window"
          className="fixed inset-x-4 bottom-4 sm:inset-auto sm:bottom-24 sm:right-6 z-50 sm:w-[380px] h-[560px] max-h-[85vh] rounded-3xl glass-dark shadow-2xl overflow-hidden flex flex-col"
        >
          <div className="px-5 py-4 border-b border-[#C9A961]/25 flex items-center gap-3 bg-[#0B1E3F]">
            <div className="w-10 h-10 rounded-full bg-[#C9A961]/15 border border-[#C9A961]/50 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#C9A961]" />
            </div>
            <div className="flex-1">
              <div className="font-serif text-base leading-tight">Izzy · NextShore</div>
              <div className="text-[11px] text-slate-400 uppercase tracking-widest">Lead advisor</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="izzy-close-btn"
              className="text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto thin-scroll px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div
                // eslint-disable-next-line react/no-array-index-key -- append-only chat messages have no stable ID
                key={`msg-${i}`}
                className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-[#C9A961] text-[#0B1E3F] rounded-br-md"
                    : "bg-[#162A4D]/80 text-slate-100 rounded-bl-md"
                }`}
              >
                {m.content || (m.role === "assistant" && sending ? "…" : "")}
              </div>
            ))}
          </div>

          <div className="border-t border-[#C9A961]/20 p-3 bg-[#0B1E3F]">
            {submitted ? (
              <p className="text-xs text-center text-slate-400 py-2">
                Thanks — your lead is saved. Feel free to keep chatting.
              </p>
            ) : (
              <form
                onSubmit={(e) => { e.preventDefault(); send(); }}
                className="flex gap-2"
              >
                <input
                  data-testid="izzy-chat-input-field"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={sending}
                  placeholder="Type your reply…"
                  className="flex-1 bg-[#162A4D]/60 border border-[#C9A961]/25 rounded-full px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#C9A961]/70"
                />
                <button
                  type="submit"
                  data-testid="izzy-chat-send-btn"
                  disabled={sending || !input.trim()}
                  className="w-11 h-11 rounded-full btn-gold flex items-center justify-center disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
